<template>
  <!--
    Dynamic vertical fit (issue #289). The gameplay screens must show their whole
    board during timed play — scrolling part of the grid or timer off-screen is
    itself a gameplay failure — so locking scroll is not enough; the content has
    to scale to the available height.

    How it works: measure the content's natural (unscaled) height against the
    viewport's height and apply transform:scale so it fits. Three nested boxes:

      .fit-viewport  the bounded space (fills its flex/100dvh parent)
      .fit-frame     a layout box sized to the *visual* (scaled) height, so the
                     viewport's scroll area is correct and centering is honest
      .fit-content   the natural-size content; only this is transform:scaled

    Down to `minScale` it scales to fit with no scroll. Below the floor (a
    viewport under the supported minimum, or extreme OS text zoom) it stops
    shrinking and the viewport scrolls instead of clipping — the agreed
    "scale to a supported minimum, scroll below it" behaviour.

    Note for callers: a transformed ancestor becomes the containing block for
    `position:fixed` descendants, so keep fixed app-chrome (e.g. a connection
    banner) OUTSIDE the slot — scale only the board content.
  -->
  <div ref="viewportRef" class="fit-viewport">
    <div ref="frameRef" class="fit-frame" :style="frameStyle">
      <div ref="contentRef" class="fit-content" :style="contentStyle">
        <slot />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';

const props = withDefaults(
  defineProps<{
    /**
     * Lower bound on the scale factor: a pragmatic floor the supported-minimum
     * viewport never reaches (a 375x667 portrait phone — the declared minimum —
     * fits at ~1). Anything that would force a smaller scale is out-of-spec
     * (a sub-minimum viewport or extreme OS text zoom); rather than shrink the
     * content to illegibility we stop here and let the viewport scroll instead.
     */
    minScale?: number;
  }>(),
  { minScale: 0.5 }
);

const viewportRef = ref<HTMLElement | null>(null);
const frameRef = ref<HTMLElement | null>(null);
const contentRef = ref<HTMLElement | null>(null);

const scale = ref(1);
const naturalHeight = ref(0);

const contentStyle = computed(() => ({
  transform: `scale(${scale.value})`,
}));

// The frame occupies the visual (scaled) height in layout. When the content
// fits, the frame is <= the viewport and gets centered; below the floor the
// frame is taller than the viewport and the viewport (overflow-y:auto) scrolls.
const frameStyle = computed(() => ({
  height: naturalHeight.value > 0 ? `${naturalHeight.value * scale.value}px` : undefined,
}));

const measure = () => {
  const viewport = viewportRef.value;
  const content = contentRef.value;
  if (!viewport || !content) return;
  const available = viewport.clientHeight;
  // offsetHeight is the unscaled layout height — transform does not affect it,
  // so reading it here cannot feed back into the scale we apply.
  const natural = content.offsetHeight;
  if (available <= 0 || natural <= 0) return;
  naturalHeight.value = natural;
  scale.value = Math.min(1, Math.max(props.minScale, available / natural));
};

let rafId = 0;
const scheduleMeasure = () => {
  if (typeof requestAnimationFrame === 'undefined') {
    measure();
    return;
  }
  cancelAnimationFrame(rafId);
  rafId = requestAnimationFrame(measure);
};

let observer: ResizeObserver | null = null;
onMounted(() => {
  if (typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver(scheduleMeasure);
    // Observe the viewport (its size changes on rotate/resize) and the content
    // (its natural height changes as letters reveal). NOT the frame — we set its
    // height, so observing it would loop.
    if (viewportRef.value) observer.observe(viewportRef.value);
    if (contentRef.value) observer.observe(contentRef.value);
  }
  window.addEventListener('resize', scheduleMeasure);
  window.addEventListener('orientationchange', scheduleMeasure);
  nextTick(measure);
});

onBeforeUnmount(() => {
  if (typeof requestAnimationFrame !== 'undefined') cancelAnimationFrame(rafId);
  observer?.disconnect();
  window.removeEventListener('resize', scheduleMeasure);
  window.removeEventListener('orientationchange', scheduleMeasure);
});
</script>

<style scoped>
.fit-viewport {
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  /* Fallback only: above the min-scale floor the frame fits and this never
     scrolls; below it the frame exceeds the viewport and this keeps content
     reachable instead of clipping it. */
  overflow-y: auto;
  overflow-x: hidden;
}

.fit-frame {
  flex: 0 0 auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  /* Clip the content's unscaled layout box (taller than this frame) so it does
     not inflate the viewport's scroll area; the scaled content fills the frame
     exactly, so nothing visible is clipped. */
  overflow: hidden;
}

.fit-content {
  width: 100%;
  /* Scale from the top edge so the visual content aligns with the frame's top;
     the frame height (natural * scale) then matches the visual height exactly. */
  transform-origin: top center;
}
</style>
